/**
 * Notices that a new build has shipped and gets this tab onto it.
 *
 * Without it a tab opened before a deploy runs the old bundle indefinitely —
 * one polled removed endpoints every 40s for twelve hours, ~45% of production
 * API traffic. `index.html` is served no-cache and assets are hashed, so a
 * plain reload is always enough; the work is deciding when one is safe.
 *
 *   - **When to look**: on becoming visible and every 10 minutes while
 *     visible. Never while hidden — a background tab costs nothing to leave
 *     stale until it is looked at, and looking is what this avoids.
 *   - **Hidden + stale + nothing unsaved → reload**, after a short settle so
 *     the editors' flush-on-hidden writes have started and reported. While
 *     work is unsettled it waits for it to settle, still hidden.
 *   - **Visible + stale → the chip** (`setUpdateAvailable`); the user reloads.
 *   - **Once per deployed version per tab session** (sessionStorage): if the
 *     reloaded bundle still mismatches, only the chip shows. Where storage
 *     throws there is no guard to record, so it never reloads unattended.
 *
 * Framework-free; `useAppUpdateChecker` mounts it once in the app shell.
 */
import { isWorkSettled, subscribeToUnsavedWork } from '@shared/status/unsavedWork';

import { setUpdateAvailable } from './appUpdateStore';
import { BUNDLE_VERSION, fetchDeployedVersion, isStaleBundle } from './appVersion';
import { reloadPage } from './reloadPage';

/** How often a visible tab re-checks. */
export const CHECK_INTERVAL_MS = 10 * 60 * 1000;
/** How long after hiding before deciding, so flush-on-hidden saves have reported. */
export const SETTLE_MS = 2_000;
/** sessionStorage key holding the version this tab last auto-reloaded for. */
export const AUTO_RELOAD_KEY = 'pa:auto-reloaded-for';

/** The checker's collaborators, injectable for tests. */
export interface AppUpdateCheckerDeps {
  bundleVersion: string;
  fetchVersion: () => Promise<string | null>;
  reload: () => void;
}

const defaultDeps: AppUpdateCheckerDeps = {
  bundleVersion: BUNDLE_VERSION,
  fetchVersion: fetchDeployedVersion,
  reload: reloadPage,
};

/** Has this tab already auto-reloaded for `version`? Unreadable storage counts as yes. */
function alreadyAutoReloaded(version: string): boolean {
  try {
    return window.sessionStorage.getItem(AUTO_RELOAD_KEY) === version;
  } catch {
    return true;
  }
}

/** Record the auto-reload for `version`; false when it cannot be recorded. */
function claimAutoReload(version: string): boolean {
  try {
    if (window.sessionStorage.getItem(AUTO_RELOAD_KEY) === version) return false;
    window.sessionStorage.setItem(AUTO_RELOAD_KEY, version);
    return true;
  } catch {
    return false;
  }
}

/** Whether the document is hidden right now. */
function isHidden(): boolean {
  return document.visibilityState === 'hidden';
}

/** Start checking; returns the stop function. Mount once per app. */
export function startAppUpdateChecker(deps: AppUpdateCheckerDeps = defaultDeps): () => void {
  let deployed: string | null = null;
  let interval: ReturnType<typeof setInterval> | null = null;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  let stopWatchingWork: (() => void) | null = null;
  let stopped = false;

  const stale = (): boolean => isStaleBundle(deployed, deps.bundleVersion);

  const cancelHiddenReload = (): void => {
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = null;
    stopWatchingWork?.();
    stopWatchingWork = null;
  };

  const scheduleAttempt = (): void => {
    if (settleTimer || stopWatchingWork) return;
    settleTimer = setTimeout(attempt, SETTLE_MS);
  };

  /** Wait — still hidden — for unsaved work and queued writes to settle. */
  const waitForSettledWork = (): void => {
    stopWatchingWork = subscribeToUnsavedWork(() => {
      if (!isWorkSettled()) return;
      stopWatchingWork?.();
      stopWatchingWork = null;
      scheduleAttempt();
    });
  };

  /** Reload if still hidden, stale and settled; otherwise wait for the work to settle. */
  function attempt(): void {
    settleTimer = null;
    if (stopped || !isHidden() || deployed === null || !stale()) return;
    if (!isWorkSettled()) {
      waitForSettledWork();
      return;
    }
    if (claimAutoReload(deployed)) deps.reload();
  }

  const maybeReloadWhileHidden = (): void => {
    if (!isHidden() || deployed === null || !stale() || alreadyAutoReloaded(deployed)) return;
    scheduleAttempt();
  };

  const check = async (): Promise<void> => {
    if (isHidden()) return;
    const version = await deps.fetchVersion();
    if (stopped || version === null) return;
    deployed = version;
    setUpdateAvailable(stale());
    maybeReloadWhileHidden();
  };

  const stopInterval = (): void => {
    if (interval) clearInterval(interval);
    interval = null;
  };

  const startInterval = (): void => {
    stopInterval();
    interval = setInterval(() => void check(), CHECK_INTERVAL_MS);
  };

  const onVisibilityChange = (): void => {
    if (isHidden()) {
      stopInterval();
      maybeReloadWhileHidden();
      return;
    }
    cancelHiddenReload();
    void check();
    startInterval();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  if (!isHidden()) {
    void check();
    startInterval();
  }

  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    stopInterval();
    cancelHiddenReload();
  };
}
